import { DatePicker, Input } from "antd";
import React, { useState } from "react";
import { useFormik } from "formik";
import { AiOutlinePlus, AiOutlineClose, AiOutlineArrowUp, AiOutlineArrowDown } from "react-icons/ai";
import dayjs from "dayjs";
import CommonButton from "../common/Button/CommonButton";
import ActionsModal from "../common/Modals/ActionsModal";
import { BackIcon } from "../Icons/BackIcon";
import {
  getTournamentEditValidationSchema,
  hasTournamentStarted,
  MAX_OPTION_LENGTH,
  MAX_TOURNAMENT_OPTIONS,
  MAX_TOURNAMENT_QUESTIONS,
} from "../../utils/validation";

const hourRange = (start, end) => {
  const hours = [];
  for (let hour = start; hour < end; hour += 1) {
    hours.push(hour);
  }
  return hours;
};

const mapQuestions = (questions = []) =>
  questions.map((item, index) => ({
    _id: item._id || null,
    clientKey: item._id || item.clientKey || `q-${index}`,
    question: item.question || "",
    options: (item.options || []).map((opt) =>
      typeof opt === "string" ? opt : opt.label
    ),
  }));

const emptyQuestion = () => ({
  question: "",
  options: ["", ""],
  _id: null,
  clientKey: `q-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
});

const TournamentEditForm = ({
  initialData,
  isCreate = false,
  onSubmit,
  onCancel,
  onPublish,
  onUnpublish,
  onRegenerate,
  onRegenerateQuestion,
  onAddAiQuestion,
  loading = false,
  publishing = false,
  unpublishing = false,
  regenerating = false,
  addingAiQuestion = false,
  regeneratingQuestionId = null,
}) => {
  const [publishModal, setPublishModal] = useState(false);
  const [regenerateModal, setRegenerateModal] = useState(false);
  const [pendingPublishPayload, setPendingPublishPayload] = useState(null);

  const originalStartDate = isCreate ? null : initialData?.start_date;
  const originalEndDate = isCreate ? null : initialData?.end_date;
  const tournamentAlreadyStarted = hasTournamentStarted(originalStartDate);
  const minSelectableEndDate =
    tournamentAlreadyStarted && originalEndDate
      ? dayjs(originalEndDate)
      : null;

  const formik = useFormik({
    enableReinitialize: !isCreate,
    initialValues: {
      title: initialData?.title || "",
      description: initialData?.description || "",
      start_date: initialData?.start_date
        ? new Date(initialData.start_date)
        : null,
      end_date: initialData?.end_date ? new Date(initialData.end_date) : null,
      questions: mapQuestions(initialData?.questions).length
        ? mapQuestions(initialData.questions)
        : [emptyQuestion()],
    },
    validationSchema: getTournamentEditValidationSchema({
      originalStartDate,
      originalEndDate,
    }),
    onSubmit: (values) => {
      onSubmit(buildPayload(values));
    },
  });

  const buildPayload = (values) => ({
    title: values.title.trim(),
    description: values.description.trim(),
    start_date: values.start_date
      ? new Date(values.start_date).toISOString()
      : null,
    end_date: values.end_date ? new Date(values.end_date).toISOString() : null,
    questions: values.questions.map((item) => ({
      _id: item._id || undefined,
      question: item.question.trim(),
      options: item.options.filter((opt) => opt.trim() !== ""),
    })),
  });

  const busy =
    loading ||
    publishing ||
    unpublishing ||
    regenerating ||
    addingAiQuestion ||
    Boolean(regeneratingQuestionId);

  const handleAddQuestion = () => {
    if (formik.values.questions.length >= MAX_TOURNAMENT_QUESTIONS) return;
    formik.setFieldValue("questions", [
      ...formik.values.questions,
      emptyQuestion(),
    ]);
  };

  const handleRemoveQuestion = (index) => {
    if (formik.values.questions.length <= 1) return;
    formik.setFieldValue(
      "questions",
      formik.values.questions.filter((_, i) => i !== index)
    );
  };

  const handleMoveQuestion = (index, direction) => {
    const nextIndex = index + direction;
    const questions = formik.values.questions;
    if (nextIndex < 0 || nextIndex >= questions.length) return;
    const updated = [...questions];
    [updated[index], updated[nextIndex]] = [updated[nextIndex], updated[index]];
    formik.setFieldValue("questions", updated);
  };

  const handleAddOption = (questionIndex) => {
    const questions = [...formik.values.questions];
    if (questions[questionIndex].options.length >= MAX_TOURNAMENT_OPTIONS) {
      return;
    }
    questions[questionIndex] = {
      ...questions[questionIndex],
      options: [...questions[questionIndex].options, ""],
    };
    formik.setFieldValue("questions", questions);
  };

  const handleRemoveOption = (questionIndex, optionIndex) => {
    const questions = [...formik.values.questions];
    if (questions[questionIndex].options.length <= 2) return;
    questions[questionIndex] = {
      ...questions[questionIndex],
      options: questions[questionIndex].options.filter(
        (_, i) => i !== optionIndex
      ),
    };
    formik.setFieldValue("questions", questions);
  };

  const handlePublishClick = async (e) => {
    e.preventDefault();
    const errors = await formik.validateForm();
    if (Object.keys(errors).length === 0) {
      setPendingPublishPayload(buildPayload(formik.values));
      setPublishModal(true);
    } else {
      formik.setTouched(
        {
          title: true,
          description: true,
          start_date: true,
          end_date: true,
          questions: true,
        },
        false
      );
    }
  };

  return (
    <div>
      <div className="flex items-center mb-6">
        <span className="cursor-pointer mr-3" onClick={onCancel}>
          <BackIcon />
        </span>
        <h2 className="text-2xl font-semibold font-poppins m-0">
          {isCreate ? "Create Tournament" : "Edit Tournament"}
        </h2>
      </div>

      <form onSubmit={formik.handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <Input
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={busy}
            />
            {formik.touched.title && formik.errors.title && (
              <p className="text-red-500 text-xs mt-1">{formik.errors.title}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-red-500">*</span>
            </label>
            <Input.TextArea
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              rows={3}
              disabled={busy}
            />
            {formik.touched.description && formik.errors.description && (
              <p className="text-red-500 text-xs mt-1">
                {formik.errors.description}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date <span className="text-red-500">*</span>
            </label>
            <DatePicker
              showTime
              value={
                formik.values.start_date
                  ? dayjs(formik.values.start_date)
                  : null
              }
              onChange={(date) =>
                formik.setFieldValue("start_date", date ? date.toDate() : null)
              }
              disabledDate={(current) =>
                current && current.isBefore(dayjs().startOf("day"))
              }
              className="w-full"
              format="YYYY-MM-DD HH:mm"
              disabled={busy}
            />
            {formik.touched.start_date && formik.errors.start_date && (
              <p className="text-red-500 text-xs mt-1">
                {formik.errors.start_date}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date <span className="text-red-500">*</span>
            </label>
            <DatePicker
              showTime={{ hideDisabledOptions: true }}
              showNow={!tournamentAlreadyStarted}
              value={
                formik.values.end_date ? dayjs(formik.values.end_date) : null
              }
              onChange={(date) => {
                formik.setFieldValue(
                  "end_date",
                  date ? date.toDate() : null
                );
                formik.setFieldTouched("end_date", true, true);
              }}
              disabledDate={(current) => {
                if (!current) return false;
                const start = formik.values.start_date
                  ? dayjs(formik.values.start_date)
                  : dayjs();
                const minDate =
                  minSelectableEndDate && minSelectableEndDate.isAfter(start)
                    ? minSelectableEndDate
                    : start;
                return current.isBefore(minDate.startOf("day"));
              }}
              disabledTime={(current) => {
                if (!minSelectableEndDate || !current) return {};
                if (!current.isSame(minSelectableEndDate, "day")) return {};
                const minHour = minSelectableEndDate.hour();
                const minMinute = minSelectableEndDate.minute();
                return {
                  disabledHours: () => hourRange(0, minHour),
                  disabledMinutes: (selectedHour) =>
                    selectedHour === minHour ? hourRange(0, minMinute) : [],
                };
              }}
              className="w-full"
              format="YYYY-MM-DD HH:mm"
              disabled={busy}
            />
            {tournamentAlreadyStarted && (
              <p className="text-gray-500 text-xs mt-1">
                This tournament has already started, so the end date can only
                stay the same or be extended.
              </p>
            )}
            {formik.touched.end_date && formik.errors.end_date && (
              <p className="text-red-500 text-xs mt-1">
                {formik.errors.end_date}
              </p>
            )}
          </div>
        </div>

        <div className="mt-8">
          <div className="flex justify-between items-center mb-1">
            <h3 className="text-lg font-semibold m-0">Questions</h3>
            {onRegenerate && (
              <CommonButton
                onClick={(e) => {
                  e.preventDefault();
                  setRegenerateModal(true);
                }}
                disabled={busy}
              >
                {regenerating ? "Regenerating..." : "Regenerate with AI"}
              </CommonButton>
            )}
          </div>
          <p className="text-gray-500 text-xs mb-4">
            Use the arrows to change question order. Save the tournament to keep
            the new order.
          </p>

          <div className="space-y-6">
            {formik.values.questions.map((item, questionIndex) => (
              <div
                key={item._id || item.clientKey || questionIndex}
                className="border rounded-lg p-4 bg-gray-50"
              >
                <div className="flex justify-between items-center mb-3 gap-2">
                  <span className="font-medium">
                    Question {questionIndex + 1}
                  </span>
                  <div className="flex items-center gap-2">
                    <span
                      className={`p-1 ${
                        questionIndex === 0 || busy
                          ? "text-gray-300 cursor-not-allowed"
                          : "cursor-pointer text-gray-600 hover:text-[#4f46e5]"
                      }`}
                      title="Move up"
                      onClick={() => {
                        if (questionIndex === 0 || busy) return;
                        handleMoveQuestion(questionIndex, -1);
                      }}
                    >
                      <AiOutlineArrowUp size={18} />
                    </span>
                    <span
                      className={`p-1 ${
                        questionIndex === formik.values.questions.length - 1 ||
                        busy
                          ? "text-gray-300 cursor-not-allowed"
                          : "cursor-pointer text-gray-600 hover:text-[#4f46e5]"
                      }`}
                      title="Move down"
                      onClick={() => {
                        if (
                          questionIndex ===
                            formik.values.questions.length - 1 ||
                          busy
                        ) {
                          return;
                        }
                        handleMoveQuestion(questionIndex, 1);
                      }}
                    >
                      <AiOutlineArrowDown size={18} />
                    </span>
                    {item._id && onRegenerateQuestion && (
                      <CommonButton
                        size="small"
                        disabled={busy}
                        onClick={(e) => {
                          e.preventDefault();
                          onRegenerateQuestion(item._id);
                        }}
                      >
                        {String(regeneratingQuestionId) === String(item._id)
                          ? "Regenerating..."
                          : "Regenerate"}
                      </CommonButton>
                    )}
                    {formik.values.questions.length > 1 && (
                      <span
                        className="cursor-pointer text-red-500 hover:text-red-700"
                        onClick={() => handleRemoveQuestion(questionIndex)}
                      >
                        <AiOutlineClose size={18} />
                      </span>
                    )}
                  </div>
                </div>

                <Input.TextArea
                  value={item.question}
                  onChange={(e) =>
                    formik.setFieldValue(
                      `questions[${questionIndex}].question`,
                      e.target.value
                    )
                  }
                  placeholder="Enter question"
                  rows={2}
                  disabled={busy}
                />
                {formik.touched.questions?.[questionIndex]?.question &&
                  formik.errors.questions?.[questionIndex]?.question && (
                    <p className="text-red-500 text-xs mt-1">
                      {formik.errors.questions[questionIndex].question}
                    </p>
                  )}

                <div className="mt-3 space-y-2">
                  {item.options.map((option, optionIndex) => (
                    <div key={optionIndex} className="flex items-center gap-2">
                      <Input
                        value={option}
                        onChange={(e) =>
                          formik.setFieldValue(
                            `questions[${questionIndex}].options[${optionIndex}]`,
                            e.target.value
                          )
                        }
                        placeholder={`Option ${optionIndex + 1}`}
                        maxLength={MAX_OPTION_LENGTH}
                        disabled={busy}
                      />
                      {item.options.length > 2 && (
                        <span
                          className="cursor-pointer text-red-500 hover:text-red-700"
                          onClick={() =>
                            handleRemoveOption(questionIndex, optionIndex)
                          }
                        >
                          <AiOutlineClose size={16} />
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                {typeof formik.errors.questions?.[questionIndex]?.options ===
                  "string" && (
                  <p className="text-red-500 text-xs mt-1">
                    {formik.errors.questions[questionIndex].options}
                  </p>
                )}

                {item.options.length < MAX_TOURNAMENT_OPTIONS && (
                  <CommonButton
                    type="dashed"
                    className="mt-3"
                    disabled={busy}
                    onClick={(e) => {
                      e.preventDefault();
                      handleAddOption(questionIndex);
                    }}
                  >
                    <AiOutlinePlus className="mr-1" /> Add Option
                  </CommonButton>
                )}
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-3 mt-4">
            <CommonButton
              type="dashed"
              disabled={
                busy ||
                formik.values.questions.length >= MAX_TOURNAMENT_QUESTIONS
              }
              onClick={(e) => {
                e.preventDefault();
                handleAddQuestion();
              }}
            >
              <AiOutlinePlus className="mr-1" /> Add Question
            </CommonButton>
            {onAddAiQuestion &&
              formik.values.questions.length < MAX_TOURNAMENT_QUESTIONS && (
                <CommonButton
                  type="primary"
                  className="bg-[#4f46e5] hover:bg-[#4338ca]"
                  disabled={busy}
                  onClick={(e) => {
                    e.preventDefault();
                    onAddAiQuestion();
                  }}
                >
                  <AiOutlinePlus className="mr-1" />
                  {addingAiQuestion
                    ? "Generating..."
                    : "Add AI Question"}
                </CommonButton>
              )}
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
          <CommonButton htmlType="button" onClick={onCancel} className="rounded-md px-6" disabled={busy}>
            Cancel
          </CommonButton>
          <CommonButton
            type="primary"
            htmlType="submit"
            disabled={busy}
            className="rounded-md px-6 bg-[#4f46e5] hover:bg-[#4338ca]"
          >
            {loading
              ? "Saving..."
              : isCreate
              ? "Save as Draft"
              : "Update"}
          </CommonButton>
          {onUnpublish && (
            <CommonButton
              htmlType="button"
              onClick={(e) => {
                e.preventDefault();
                onUnpublish();
              }}
              disabled={busy}
              className="rounded-md px-6"
            >
              {unpublishing ? "Moving..." : "Move to Draft"}
            </CommonButton>
          )}
          {onPublish && (
            <CommonButton
              type="primary"
              onClick={handlePublishClick}
              disabled={busy}
              className="rounded-md px-6 bg-[#16a34a] hover:bg-[#15803d]"
            >
              {publishing ? "Publishing..." : "Publish"}
            </CommonButton>
          )}
        </div>
      </form>

      {publishModal && (
        <ActionsModal
          title=""
          type="save"
          isOpen={publishModal}
          onCancel={() => {
            setPublishModal(false);
            setPendingPublishPayload(null);
          }}
          onOk={() => {
            setPublishModal(false);
            if (pendingPublishPayload) {
              onPublish(pendingPublishPayload);
              setPendingPublishPayload(null);
            }
          }}
          footer={false}
          centered={true}
          closable={false}
          maskClosable={false}
          className="delete-modal"
          cancelBtnClass="cancleBtnAction !bg-gray-200 !text-gray-700 hover:!bg-gray-300 !border-gray-300"
          cancelBtnClick={() => {
            setPublishModal(false);
            setPendingPublishPayload(null);
          }}
          cancelButtonProps="Cancel"
          saveBtnClass="saveBtnAction !bg-[#16a34a] hover:!bg-[#15803d] !border-[#16a34a]"
          saveBtnClick={() => {
            setPublishModal(false);
            if (pendingPublishPayload) {
              onPublish(pendingPublishPayload);
              setPendingPublishPayload(null);
            }
          }}
          saveButtonProps="Publish"
          savebtnType={false}
          description="This tournament will be visible to users. You can still edit or delete it later."
          mainTitle="Publish Tournament?"
        />
      )}

      {regenerateModal && (
        <ActionsModal
          title=""
          type="save"
          isOpen={regenerateModal}
          onCancel={() => setRegenerateModal(false)}
          onOk={() => {
            setRegenerateModal(false);
            onRegenerate();
          }}
          footer={false}
          centered={true}
          closable={false}
          maskClosable={false}
          className="delete-modal"
          cancelBtnClass="cancleBtnAction !bg-gray-200 !text-gray-700 hover:!bg-gray-300 !border-gray-300"
          cancelBtnClick={() => setRegenerateModal(false)}
          cancelButtonProps="Cancel"
          saveBtnClass="saveBtnAction !bg-[#4f46e5] hover:!bg-[#4338ca] !border-[#4f46e5]"
          saveBtnClick={() => {
            setRegenerateModal(false);
            onRegenerate();
          }}
          saveButtonProps="Regenerate"
          savebtnType={false}
          description="This will replace all current questions with a new AI-generated set. Unsaved edits will be lost."
          mainTitle="Regenerate Questions?"
        />
      )}
    </div>
  );
};

export default TournamentEditForm;
